/**
 * Auth guard — wraps protected routes.
 *
 * Behavior (T7 AC #2):
 *   - hydrating     → render `fallback` (default spinner)
 *   - authenticated → render children
 *   - unauthenticated → redirect to /login, preserving the destination path
 *     in `state.from` so a future T8 post-login step can bounce the user
 *     back. (For now `LoginPage` ignores it — we just keep the contract.)
 *
 * T16 Stage 4 Bug 1 fix: when an unauthenticated user clicks an invite link
 * such as `https://dev.shopista.ca/?invite=<token>`, the token must survive
 * this redirect to /login. Previously we only stashed the original query in
 * `state.from` — but `state` is React-Router-only state, and the LoginPage
 * reads `?invite=` from the URL via `useSearchParams()`. The bare `/login`
 * URL meant the param was effectively lost on every email-link click, so
 * sign-in fired without `inviteToken` and the backend created a default
 * household. We now propagate the `invite` query param onto the redirect
 * URL itself so LoginPage's existing reader picks it up.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from './session';
import type { WebTokenStorage } from './tokenStorage';
import { routes } from '../routes/paths';
import { Button } from '../components/Button';

export interface AuthGuardProps {
  tokens: WebTokenStorage;
  children: ReactNode;
  /** Element shown while `/auth/me` is in flight. Default is a plain div. */
  fallback?: ReactNode;
}

export function AuthGuard({
  tokens,
  children,
  fallback,
}: AuthGuardProps): JSX.Element {
  const location = useLocation();
  const session = useSession({ tokens });

  if (session.status === 'hydrating') {
    return (
      <div data-testid="auth-hydrating" style={{ padding: '2rem' }}>
        {fallback ?? 'Loading...'}
      </div>
    );
  }

  if (session.status === 'error-retryable') {
    // A transient cold-`/auth/me` failure (backend bouncing, network blip). The
    // session is INTACT — show a retry, never redirect to /login. Redirecting
    // here would, on native, durably wipe the Keystore session for a passing
    // outage (the bug this task fixes). T27.2.2.2.
    return (
      <div
        data-testid="auth-retry"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-6)',
          textAlign: 'center',
        }}
      >
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Can&rsquo;t reach Shopista right now. Your session is still active.
        </p>
        <Button
          type="button"
          variant="primary"
          data-testid="auth-retry-button"
          onClick={session.retry}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (session.status === 'unauthenticated') {
    // Propagate `?invite=<token>` onto the /login URL so LoginPage's
    // useSearchParams reader (`params.get('invite')`) picks it up. Without
    // this, an email-link click for an unauthenticated user loses the
    // invite token between AuthGuard and LoginPage.
    const params = new URLSearchParams(location.search);
    const invite = params.get('invite');
    const loginPath = invite
      ? `${routes.login}?invite=${encodeURIComponent(invite)}`
      : routes.login;
    return (
      <Navigate
        to={loginPath}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <>{children}</>;
}
